import { Injectable, signal, computed, inject } from '@angular/core';
import { Observable, catchError, of, tap, finalize, throwError } from 'rxjs';
import { LoggingService } from '../shared/logging/logging.service';
import { ToastrService } from 'ngx-toastr';

// API Client imports
import {
  ModulesGroupsApplicationPairingsBlockDto as BlockDto,
  ModulesGroupsApplicationPairingsGroupDto as GroupDto,
  ModulesGroupsApplicationPairingsGroupSlotDto as GroupSlotDto,
  ModulesGroupsApplicationPairingsAvailablePlayerDto as AvailablePlayerDto,
  ModulesGroupsApplicationPairingsPairingSettingsDto as PairingsSettingsDto,
  ModulesGroupsApplicationPairingsGetPairingsPairingsResponse as PairingsResponse,
  ModulesGroupsApplicationPairingsRoundTournamentCourseDto as RoundTournamentCourseDto
} from '../../api-client/model/models';
import { PairingsService as ApiPairingsService } from '../../api-client/api/pairings.service';

export interface PairingsState {
  selectedRoundTournamentCourseId: number | null;
  pairings: PairingsResponse | null;
  availableCourses: RoundTournamentCourseDto[];
  isLoading: boolean;
  isGenerating: boolean;
  error: string | null;
}

export interface PairingsSwapPlayerOption {
  slotId: string;
  groupId: string;
  playerName: string;
  displayDetail: string;
  groupNumber: number | null;
  tee: number | null;
  teeTime: string;
  blockNumber: number | null;
  wave: string | null;
}

export interface PairingsSwapGroupOption {
  groupId: string;
  displayName: string;
  detail: string;
  blockNumber: number | null;
  wave: string | null;
  groupNumber: number | null;
}

export interface PairingsSwapHistoryEntry {
  id: string;
  description: string;
  timestamp: Date;
}

@Injectable({
  providedIn: 'root'
})
export class PairingsService {
  private readonly apiService = inject(ApiPairingsService);
  private readonly logger = inject(LoggingService);
  private readonly toastr = inject(ToastrService);
  private pendingRequests = 0;

  // State signals
  private readonly selectedRoundTournamentCourseIdSignal = signal<number | null>(null);
  private readonly pairingsSignal = signal<PairingsResponse | null>(null);
  private readonly availableCoursesSignal = signal<RoundTournamentCourseDto[]>([]);
  private readonly isLoadingSignal = signal<boolean>(false);
  private readonly isGeneratingSignal = signal<boolean>(false);
  private readonly errorSignal = signal<string | null>(null);
  private readonly swapHistorySignal = signal<PairingsSwapHistoryEntry[]>([]);

  // Public readonly signals
  readonly selectedRoundTournamentCourseId = this.selectedRoundTournamentCourseIdSignal.asReadonly();
  readonly pairings = this.pairingsSignal.asReadonly();
  readonly availableCourses = this.availableCoursesSignal.asReadonly();
  readonly isLoading = this.isLoadingSignal.asReadonly();
  readonly isGenerating = this.isGeneratingSignal.asReadonly();
  readonly error = this.errorSignal.asReadonly();
  readonly swapHistory = this.swapHistorySignal.asReadonly();

  // Computed signals
  readonly hasError = computed(() => this.error() !== null);
  readonly hasPairings = computed(() => this.pairings() !== null);
  readonly selectedCourse = computed(() => {
    const id = this.selectedRoundTournamentCourseId();
    return id ? this.availableCourses().find(c => c.roundTournamentCourseId === id) : null;
  });
  readonly blocks = computed(() => this.pairings()?.blocks ?? []);
  readonly availablePlayers = computed(() => this.pairings()?.availablePlayers ?? []);
  readonly settings = computed(() => this.pairings()?.settings ?? null);
  readonly isPairingsPublished = computed(() => {
    const settings = this.settings();
    return settings?.pairingsLastPublished !== null && settings?.pairingsLastPublished !== undefined;
  });

  // State computed signal
  readonly state = computed<PairingsState>(() => ({
    selectedRoundTournamentCourseId: this.selectedRoundTournamentCourseId(),
    pairings: this.pairings(),
    availableCourses: this.availableCourses(),
    isLoading: this.isLoading(),
    isGenerating: this.isGenerating(),
    error: this.error()
  }));

  constructor() {
    this.logger.info('PairingsService', 'PairingsService initialized');
  }

  readonly swapPlayerOptions = computed<PairingsSwapPlayerOption[]>(() => {
    const options: PairingsSwapPlayerOption[] = [];

    this.blocks().forEach(block => {
      const blockNumber = block?.number ?? null;
      const wave = block?.wave ?? null;

      block?.groups?.forEach(group => {
        const groupId = group?.groupId;
        if (!groupId) {
          return;
        }

        const groupNumber = group.number ?? null;
        const tee = group.startTee ?? null;
        const teeTimeLabel = this.formatTimeLabel(group.startTime);

        group.slots?.forEach(slot => {
          const slotId = slot?.slotId;
          if (!slotId || !slot?.playerName) {
            return;
          }

          const detailParts = [
            `Grp ${groupNumber ?? '—'}`,
            `Tee ${tee ?? '—'}`,
            teeTimeLabel || '—'
          ];

          if (slot.number !== undefined && slot.number !== null) {
            detailParts.push(`Slot ${slot.number}`);
          }

          const contextParts: string[] = [];
          if (blockNumber !== null) {
            contextParts.push(`Block ${blockNumber}`);
          }
          if (wave) {
            contextParts.push(`Wave ${wave}`);
          }

          const displayDetail = contextParts.length > 0
            ? `${detailParts.join(' • ')} (${contextParts.join(' • ')})`
            : detailParts.join(' • ');

          options.push({
            slotId,
            groupId,
            playerName: slot.playerName,
            displayDetail,
            groupNumber,
            tee,
            teeTime: teeTimeLabel,
            blockNumber,
            wave
          });
        });
      });
    });

    return options.sort((a, b) => a.playerName.localeCompare(b.playerName, undefined, { sensitivity: 'base' }));
  });

  readonly swapGroupOptions = computed<PairingsSwapGroupOption[]>(() => {
    const options: PairingsSwapGroupOption[] = [];

    this.blocks().forEach(block => {
      const blockNumber = block?.number ?? null;
      const wave = block?.wave ?? null;

      block?.groups?.forEach(group => {
        const groupId = group?.groupId;
        if (!groupId) {
          return;
        }

        const groupNumber = group.number ?? null;
        const tee = group.startTee ?? null;
        const teeTimeLabel = this.formatTimeLabel(group.startTime);

        const nameParts = [`Group ${groupNumber ?? '—'}`];
        if (tee !== null && tee !== undefined) {
          nameParts.push(`Tee ${tee}`);
        }
        if (teeTimeLabel) {
          nameParts.push(teeTimeLabel);
        }

        const contextParts: string[] = [];
        if (blockNumber !== null) {
          contextParts.push(`Block ${blockNumber}`);
        }
        if (wave) {
          contextParts.push(`Wave ${wave}`);
        }

        const detail = contextParts.length > 0 ? contextParts.join(' • ') : '—';

        options.push({
          groupId,
          displayName: nameParts.join(' • '),
          detail,
          blockNumber,
          wave,
          groupNumber
        });
      });
    });

    return options.sort((a, b) => {
      if (a.blockNumber !== null && b.blockNumber !== null && a.blockNumber !== b.blockNumber) {
        return a.blockNumber - b.blockNumber;
      }

      return (a.groupNumber ?? 0) - (b.groupNumber ?? 0);
    });
  });
  /**
   * Load available courses for a round
   */
  loadCoursesForRound(roundId: string): Observable<RoundTournamentCourseDto[]> {
    this.logger.info('PairingsService', `Loading courses for round ${roundId}`);
    this.beginLoading();
    this.errorSignal.set(null);

    return this.apiService.getRoundTournamentCoursesEndpoint(roundId as any).pipe(
      tap(courses => {
        this.availableCoursesSignal.set(courses);
        this.logger.info('PairingsService', `Loaded ${courses.length} courses`);
        this.syncSelectedCourse(courses);
      }),
      catchError(error => {
        const message = 'Failed to load courses';
        this.logger.error(message, error);
        this.errorSignal.set(message);
        this.toastr.error(message);
        this.availableCoursesSignal.set([]);
        this.syncSelectedCourse([]);
        return of([]);
      }),
      finalize(() => this.endLoading())
    );
  }

  /**
   * Retrieve courses for a round without mutating local state. Used when copying from another round.
   */
  getCoursesSnapshotForRound(roundId: string): Observable<RoundTournamentCourseDto[]> {
    this.logger.info('PairingsService', `Fetching course snapshot for round ${roundId}`);

    return this.apiService.getRoundTournamentCoursesEndpoint(roundId as any).pipe(
      catchError(error => {
        this.logger.error('PairingsService', 'Failed to load courses for snapshot', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Select a round tournament course and load its pairings
   */
  selectCourse(roundTournamentCourseId: number): void {
    this.logger.info('PairingsService', `Selecting course ${roundTournamentCourseId}`);
    this.swapHistorySignal.set([]);
    this.selectedRoundTournamentCourseIdSignal.set(roundTournamentCourseId);
    this.loadPairings(roundTournamentCourseId).subscribe();
  }

  /**
   * Load pairings for a round tournament course
   */
  loadPairings(roundTournamentCourseId: number): Observable<PairingsResponse> {
    this.logger.info('PairingsService', `Loading pairings for course ${roundTournamentCourseId}`);
    this.beginLoading();
    this.errorSignal.set(null);

    return this.apiService.getPairingsEndpoint(roundTournamentCourseId).pipe(
      tap(pairings => {
        this.pairingsSignal.set(pairings);
        this.logger.info('PairingsService', `Loaded pairings with ${pairings.blocks?.length ?? 0} blocks`);
      }),
      catchError(error => {
        const message = 'Failed to load pairings';
        this.logger.error(message, error);
        this.errorSignal.set(message);
        this.toastr.error(message);
        return of({} as PairingsResponse);
      }),
      finalize(() => this.endLoading())
    );
  }

  /**
   * Generate pairings for the selected course
   */
  generatePairings(): Observable<void> {
    const roundTournamentCourseId = this.selectedRoundTournamentCourseId();
    if (!roundTournamentCourseId) {
      this.toastr.warning('Please select a course first');
      return of(void 0);
    }

    this.logger.info('PairingsService', `Generating pairings for course ${roundTournamentCourseId}`);
    this.isGeneratingSignal.set(true);
    this.errorSignal.set(null);

    return this.apiService.generatePairingsEndpoint({ roundTournamentCourseId }).pipe(
      tap(() => {
        this.logger.info('PairingsService', 'Pairings generated successfully');
        this.toastr.success('Pairings generated successfully');
        // Reload pairings
        this.loadPairings(roundTournamentCourseId).subscribe();
      }),
      catchError(error => {
        let message = 'Failed to generate pairings';
        
        // Check if error is related to missing settings
        if (error?.error?.detail?.includes('Number of players must be greater than 0') ||
            error?.error?.type?.includes('InvalidNumberOfPlayers')) {
          message = 'Pairing settings have not been saved yet. Please:\n1. Click the Settings button\n2. Review the settings (Players in Field should be set)\n3. Click Save Settings\n4. Then try Generate Pairings again';
        } else if (error?.error?.detail) {
          message = `Failed to generate pairings: ${error.error.detail}`;
        }
        
        this.logger.error(message, error);
        this.errorSignal.set(message);
        this.toastr.error(message, 'Configuration Required', { timeOut: 12000, enableHtml: true });
        return of(void 0);
      }),
      finalize(() => this.isGeneratingSignal.set(false))
    );
  }

  /**
   * Clear pairings for the selected course
   */
  clearPairings(slotsOnly: boolean = false): Observable<void> {
    const roundTournamentCourseId = this.selectedRoundTournamentCourseId();
    if (!roundTournamentCourseId) {
      this.toastr.warning('Please select a course first');
      return of(void 0);
    }

    this.logger.info('PairingsService', `Clearing pairings for course ${roundTournamentCourseId}, slotsOnly: ${slotsOnly}`);
    this.beginLoading();
    this.errorSignal.set(null);

    return this.apiService.clearPairingsEndpoint({
      roundTournamentCourseId,
      clearGroupsOnly: slotsOnly
    }).pipe(
      tap(() => {
        this.logger.info('PairingsService', 'Pairings cleared successfully');
        this.toastr.success(slotsOnly ? 'Player assignments cleared' : 'Pairings cleared successfully');
        // Reload pairings
        this.loadPairings(roundTournamentCourseId).subscribe();
      }),
      catchError(error => {
        const message = 'Failed to clear pairings';
        this.logger.error(message, error);
        this.errorSignal.set(message);
        this.toastr.error(message);
        return of(void 0);
      }),
      finalize(() => this.endLoading())
    );
  }

  /**
   * Publish pairings for the selected course
   */
  publishPairings(): Observable<void> {
    const roundTournamentCourseId = this.selectedRoundTournamentCourseId();
    if (!roundTournamentCourseId) {
      this.toastr.warning('Please select a course first');
      return of(void 0);
    }

    this.logger.info('PairingsService', `Publishing pairings for course ${roundTournamentCourseId}`);
    this.beginLoading();
    this.errorSignal.set(null);

    return this.apiService.publishPairingsEndpoint({ roundTournamentCourseId }).pipe(
      tap(() => {
        this.logger.info('PairingsService', 'Pairings published successfully');
        this.toastr.success('Pairings published successfully');
        // Reload pairings to get updated publish timestamp
        this.loadPairings(roundTournamentCourseId).subscribe();
      }),
      catchError(error => {
        const message = 'Failed to publish pairings';
        this.logger.error(message, error);
        this.errorSignal.set(message);
        this.toastr.error(message);
        return of(void 0);
      }),
      finalize(() => this.endLoading())
    );
  }

  /**
   * Copy pairings from source to target course
   */
  copyPairings(sourceRoundTournamentCourseId: number, targetRoundTournamentCourseId: number): Observable<void> {
    this.logger.info('PairingsService', `Copying pairings from ${sourceRoundTournamentCourseId} to ${targetRoundTournamentCourseId}`);
    this.beginLoading();
    this.errorSignal.set(null);

    return this.apiService.copyPairingsEndpoint({
      sourceRoundTournamentCourseId,
      targetRoundTournamentCourseId
    }).pipe(
      tap(() => {
        this.logger.info('PairingsService', 'Pairings copied successfully');
        this.toastr.success('Pairings copied successfully');
        // Reload pairings if viewing the target course
        if (this.selectedRoundTournamentCourseId() === targetRoundTournamentCourseId) {
          this.loadPairings(targetRoundTournamentCourseId).subscribe();
        }
      }),
      catchError(error => {
        const message = 'Failed to copy pairings';
        this.logger.error(message, error);
        this.errorSignal.set(message);
        this.toastr.error(message);
        return of(void 0);
      }),
      finalize(() => this.endLoading())
    );
  }

  /**
   * Update pairing settings for a round tournament course
   */
  updateSettings(roundTournamentCourseId: number, settings: Record<string, string | null>): Observable<void> {
    this.logger.info('PairingsService', `Updating settings for course ${roundTournamentCourseId}`, settings);
    this.beginLoading();
    this.errorSignal.set(null);

    // Filter out null values from settings as the API expects only string values
    const filteredSettings: { [key: string]: string } = Object.entries(settings)
      .filter(([_, value]) => value !== null)
      .reduce((acc, [key, value]) => ({ ...acc, [key]: value as string }), {});

    return this.apiService.savePairingSettingsEndpoint({
      roundTournamentCourseId,
      settings: filteredSettings
    }).pipe(
      tap(() => {
        this.logger.info('PairingsService', 'Settings saved successfully');
        this.toastr.success('Settings saved successfully');
        // Reload pairings to get updated settings
        this.loadPairings(roundTournamentCourseId).subscribe();
      }),
      catchError(error => {
        const message = 'Failed to save settings';
        this.logger.error(message, error);
        this.errorSignal.set(message);
        this.toastr.error(message);
        return of(void 0);
      }),
      finalize(() => this.endLoading())
    );
  }

  swapPlayers(firstSlotId: string, secondSlotId: string): Observable<void> {
    if (!firstSlotId || !secondSlotId) {
      this.toastr.warning('Select two players to swap');
      return of(void 0);
    }

    if (firstSlotId === secondSlotId) {
      this.toastr.warning('Select two different players to swap');
      return of(void 0);
    }

    const roundTournamentCourseId = this.selectedRoundTournamentCourseId();
    if (!roundTournamentCourseId) {
      this.toastr.warning('Please select a course first');
      return of(void 0);
    }

    this.beginLoading();
    this.errorSignal.set(null);

    return this.apiService.swapPlayersEndpoint({
      firstSlotId,
      secondSlotId
    }).pipe(
      tap(() => {
        this.logger.info('PairingsService', 'Players swapped successfully');
        this.toastr.success('Players swapped successfully');
        this.appendSwapHistory(this.buildPlayerSwapDescription(firstSlotId, secondSlotId));
        this.loadPairings(roundTournamentCourseId).subscribe();
      }),
      catchError(error => {
        const message = 'Failed to swap players';
        this.logger.error(message, error);
        this.errorSignal.set(message);
        this.toastr.error(message);
        return of(void 0);
      }),
      finalize(() => this.endLoading())
    );
  }

  swapGroups(firstGroupId: string, secondGroupId: string): Observable<void> {
    if (!firstGroupId || !secondGroupId) {
      this.toastr.warning('Select two groups to swap');
      return of(void 0);
    }

    if (firstGroupId === secondGroupId) {
      this.toastr.warning('Select two different groups to swap');
      return of(void 0);
    }

    const roundTournamentCourseId = this.selectedRoundTournamentCourseId();
    if (!roundTournamentCourseId) {
      this.toastr.warning('Please select a course first');
      return of(void 0);
    }

    this.beginLoading();
    this.errorSignal.set(null);

    return this.apiService.swapGroupsEndpoint({
      firstGroupId,
      secondGroupId
    }).pipe(
      tap(() => {
        this.logger.info('PairingsService', 'Groups swapped successfully');
        this.toastr.success('Groups swapped successfully');
        this.appendSwapHistory(this.buildGroupSwapDescription(firstGroupId, secondGroupId));
        this.loadPairings(roundTournamentCourseId).subscribe();
      }),
      catchError(error => {
        const message = 'Failed to swap groups';
        this.logger.error(message, error);
        this.errorSignal.set(message);
        this.toastr.error(message);
        return of(void 0);
      }),
      finalize(() => this.endLoading())
    );
  }

  /**
   * Reset state
   */
  reset(): void {
    this.logger.info('PairingsService', 'Resetting pairings state');
    this.selectedRoundTournamentCourseIdSignal.set(null);
    this.pairingsSignal.set(null);
    this.availableCoursesSignal.set([]);
    this.errorSignal.set(null);
    this.pendingRequests = 0;
    this.isLoadingSignal.set(false);
    this.swapHistorySignal.set([]);
  }

  private beginLoading(): void {
    this.pendingRequests++;
    if (this.pendingRequests === 1) {
      this.isLoadingSignal.set(true);
    }
  }

  private endLoading(): void {
    if (this.pendingRequests === 0) {
      return;
    }

    this.pendingRequests--;

    if (this.pendingRequests === 0) {
      this.isLoadingSignal.set(false);
    }
  }

  private syncSelectedCourse(courses: RoundTournamentCourseDto[]): void {
    if (!courses.length) {
      this.selectedRoundTournamentCourseIdSignal.set(null);
      this.pairingsSignal.set(null);
      return;
    }

    const current = this.selectedRoundTournamentCourseIdSignal();
    const hasCurrent = current !== null && courses.some(course => course.roundTournamentCourseId === current);

    if (hasCurrent) {
      return;
    }

    const firstCourseId = courses[0]?.roundTournamentCourseId;

    if (firstCourseId === null || firstCourseId === undefined) {
      return;
    }

    this.selectCourse(firstCourseId);
  }

  private formatTimeLabel(value: Date | string | undefined): string {
    if (!value) {
      return '';
    }

    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '';
    }

    return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  }

  private appendSwapHistory(description: string): void {
    if (!description) {
      return;
    }

    const entry: PairingsSwapHistoryEntry = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      description,
      timestamp: new Date()
    };

    this.swapHistorySignal.update(history => [entry, ...history].slice(0, 15));
  }

  private buildPlayerSwapDescription(firstSlotId: string, secondSlotId: string): string {
    const options = this.swapPlayerOptions();
    const first = options.find(option => option.slotId === firstSlotId);
    const second = options.find(option => option.slotId === secondSlotId);

    if (!first || !second) {
      return 'Swapped two players';
    }

    return `${first.playerName} (${first.displayDetail}) swapped with ${second.playerName} (${second.displayDetail})`;
  }

  private buildGroupSwapDescription(firstGroupId: string, secondGroupId: string): string {
    const options = this.swapGroupOptions();
    const first = options.find(option => option.groupId === firstGroupId);
    const second = options.find(option => option.groupId === secondGroupId);

    if (!first || !second) {
      return 'Swapped two groups';
    }

    return `${first.displayName} ↔ ${second.displayName}`;
  }
}
